import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {NgForm} from "@angular/forms";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../shared/services/auth.service";
import {FilterService} from "../../../shared/services/filter.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {ActivatedRoute, Router} from "@angular/router";
import {delay, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {LogDeletedData} from "../../../shared/interface/log-deleted-data";
import {LogDeletedDataService} from "../../../shared/services/log-deleted-data.service";
import {Sort} from "@angular/material/sort";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {ENTITY_TYPES, SOFT_DELETED_ENTITY} from "./log-deleted-data-list.entity-types";
import {HttpErrorResponse} from "@angular/common/http";

@Component({
  selector: 'app-log-deleted-data',
  templateUrl: './log-deleted-data-list.component.html',
  styleUrls: ['./log-deleted-data-list.component.scss']
})
export class LogDeletedDataListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;

  readonly ENTITY_TYPES = ENTITY_TYPES;
  readonly SOFT_DELETED_ENTITY = SOFT_DELETED_ENTITY;

  initialFilters: any;
  displayedColumns: string[] = ['entity', 'createdAt', 'createdBy', 'note', 'actions'];
  logs: LogDeletedData[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'createdAt';
  sortOrder = 'DESC';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  public constructor(
    private deletedService: LogDeletedDataService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.deletedService.getLogDeletedDataListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: CountedAndPaginated<LogDeletedData>) => {
        this.logs = result.items;
        this.totalData = result.pagination.total;
      });
    this.parseFilters();
  }

  ngOnInit(): void {
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      entity: 'string',
      username: 'string',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'createdAt';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'DESC';
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      ...this.filtersForm.value,
    })]);
  }

  loadListData(): void {
    this.deletedService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      }
    );
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams( {
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value
    })]);
  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;
    this.loadListData();
  }

  restoreRecord(ldd: LogDeletedData): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz przywrócić usunięty element?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deletedService.restoreDeletedRecord(ldd.id)
          .subscribe(() => {
            this.snack.open('Pomyślnie przywrócono usunięty element', '', {duration: 2000});
            this.loadListData();
          }, (error: HttpErrorResponse) => {
            this.snack.open(error.error.error.text, error.status?.toString(), {
              duration: 2000,
            });
          }
          );
      }
    });
  }

  getEntityLabel(value: string): string {
    return this.ENTITY_TYPES.find(e => e.value === value)?.label || value;
  }

  isSoftDeletedEntity(value: string): boolean {
    return this.SOFT_DELETED_ENTITY.some(e => e.value === value);
  }
}
